import { useRef, useState } from 'react';
import { Section } from '@/components/layout/Section';
import { PageHero } from '@/components/brand/PageHero';
import { Watermark } from '@/components/brand/Watermark';
import { SplitText } from '@/components/motion/SplitText';
import { Reveal } from '@/components/motion/Reveal';
import { StaggerGroup } from '@/components/motion/StaggerGroup';
import { Button } from '@/components/ui/Button';
import { pricing } from '@/content/pricing';
import { usePageMeta } from '@/lib/usePageMeta';

export default function Pricing() {
  usePageMeta(
    'App Development Pricing',
    'Compare AI application, MVP and commercial software scopes. Request a written quote with deliverables, timeline, currency and ongoing costs agreed before work begins.',
    'Pricing',
  );

  const [active, setActive] = useState(0);
  const tabsRef = useRef<HTMLDivElement>(null);
  const categories = pricing.categories;
  const category = categories[active]!;

  /*
    Roving focus across the tabs, per the APG tablist pattern: arrows move
    between tabs and move focus with them, Home/End jump to the ends. Without
    this a keyboard user can reach the first tab but never the others.
  */
  function onTabKeyDown(e: React.KeyboardEvent) {
    const last = categories.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = active === last ? 0 : active + 1;
    else if (e.key === 'ArrowLeft') next = active === 0 ? last : active - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;

    e.preventDefault();
    setActive(next);
    tabsRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  }

  return (
    <>
      {/* Page hero — the same block as About, Services and Careers. */}
      <PageHero compact label="Pricing" headline="A clear scope. A clear price." description={pricing.hero.subcopy} action="Compare project scopes" target="#plans" note="A written figure before any work starts." />

      <Section size="sm" tone="muted">
        <h2 className="text-xl font-semibold">Know the full cost before you commit.</h2>
        <p className="mt-4 max-w-[75ch] leading-relaxed text-ink/75">The scopes below are discussion guides. Your proposal confirms the price and currency, platform coverage, delivery milestones and support period. Hosting, AI provider usage, paid integrations and app store fees are identified separately where applicable. Ongoing maintenance is agreed separately.</p>
      </Section>
      {/* Tiers */}
      <Section id="plans">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal className="max-w-[46ch]">
            <p className="text-eyebrow opacity-65">Plan your project</p>
            <h2 className="text-display-md mt-4">Find the right scope</h2>
            <p className="text-lead mt-5 opacity-75">{category.intro}</p>
          </Reveal>

          {/* Category toggle. */}
          <Reveal delay={0.1} className="w-full sm:w-auto">
            <div
              ref={tabsRef}
              role="tablist"
              aria-label="Project type"
              onKeyDown={onTabKeyDown}
              className="pricing-tabs grid grid-cols-3 gap-1 rounded-2xl border border-hairline bg-surface-muted p-1 sm:inline-flex sm:rounded-full"
            >
              {categories.map((c, i) => (
                <button
                  key={c.id}
                  role="tab"
                  type="button"
                  id={`tab-${c.id}`}
                  aria-selected={i === active}
                  aria-controls={`panel-${c.id}`}
                  // Roving tabindex: only the active tab is in the tab order.
                  tabIndex={i === active ? 0 : -1}
                  onClick={() => setActive(i)}
                  className={`min-h-11 rounded-xl px-2 text-xs sm:rounded-full sm:px-5 sm:text-sm font-medium transition-colors duration-500 ease-brand ${
                    i === active ? 'bg-ink text-white' : 'text-ink/60 hover:text-ink'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div
          role="tabpanel"
          id={`panel-${category.id}`}
          aria-labelledby={`tab-${category.id}`}
          /*
            key remounts the panel on category change, so StaggerGroup replays
            its reveal for the new cards instead of swapping text inside
            elements that have already animated in.
          */
          key={category.id}
        >
          <StaggerGroup className="mt-14 grid items-stretch gap-6 md:grid-cols-2 lg:grid-cols-3">
            {category.tiers.map((tier) => (
              <div
                key={tier.name}
                /*
                  The recommended tier inverts to navy rather than just gaining
                  a ring. Three near-identical light cards gave the eye nothing
                  to land on; a dark card among light ones is unmissable, and
                  the amber badge is the one warm note on the page.
                */
                className={`hover-card relative flex h-full flex-col rounded-card border p-8 ${
                  tier.featured
                    ? 'border-transparent bg-linear-to-b from-ink to-ink-3 text-white shadow-lift'
                    : 'border-hairline bg-surface-muted'
                }`}
              >
                {tier.featured && (
                  <span className="text-eyebrow absolute -top-3 left-8 rounded-full bg-accent px-3 py-1.5 font-semibold text-ink">
                    Expanded scope
                  </span>
                )}

                <h3 className="font-display text-2xl font-bold" style={{ fontStretch: '100%' }}>
                  {tier.name}
                </h3>
                <p className={`text-caption mt-2 ${tier.featured ? 'text-white/60' : 'opacity-60'}`}>
                  {tier.blurb}
                </p>

                <p className={`mt-6 text-sm ${tier.featured ? 'text-white/75' : 'text-ink/70'}`}>{tier.priceNote}</p>
                <p className="mt-2 text-2xl font-semibold">Request a quote</p>

                <ul className="mt-7 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex gap-3 text-sm leading-relaxed">
                      <span
                        aria-hidden="true"
                        className={`mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full ${
                          tier.featured ? 'bg-accent' : 'bg-accent-ink'
                        }`}
                      />
                      <span className={tier.featured ? 'text-white/85' : 'opacity-80'}>{f}</span>
                    </li>
                  ))}
                </ul>

                {/*
                  Opens the booking rather than the contact form: someone
                  reading a price has already decided what they want, so the
                  next step is a conversation, not a blank message box.
                */}
                {/* Spacer, so the three buttons line up along the bottom even
                    though the feature lists are different lengths. */}
                <div className="mt-8 grow" />
                <Button
                  href="/contact"
                  variant={tier.featured ? 'inverse' : 'secondary'}
                  className="w-full"
                >
                  {tier.cta}
                </Button>
              </div>
            ))}
          </StaggerGroup>
        </div>
      </Section>

      {/* Not sure which */}
      <Section tone="gradient">
        <Watermark position="right" scale={1.5} opacity={0.05} />
        <div className="relative z-10 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <SplitText
              as="h2"
              text={pricing.note.heading}
              className="text-display-md max-w-[16ch] leading-[1.08]"
            />
            <Reveal delay={0.25}>
              <p className="text-body-lg mt-5 max-w-[52ch] text-white/70">{pricing.note.body}</p>
            </Reveal>
          </div>
          <Reveal delay={0.4} className="shrink-0">
            <Button href={pricing.note.cta.href} variant="inverse" size="lg">
              {pricing.note.cta.label}
            </Button>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
